import { ErrorOutline as ExclamationCircleOutlined } from "@mui/icons-material";
import { DatePicker } from "../../../DatePicker/DatePicker";
import { Tooltip } from "../../../Tooltip/Tooltip";
import { useCallback } from "react";
import { Metadata } from "../../types";
import FieldSuggestionChip from "../FieldSuggestionChip.component";
import { useTemplateController } from "../FieldContext";
import { getFormRulesFromMetadata } from "../utils";
import { InputFieldProps } from "./InputFieldBase";
import dayjs, { type Dayjs } from "dayjs";
import { formatDate } from "../../../../utils/date";

type PickerType = "week" | "month" | "quarter" | "year";

const DATE_DISPLAY_FORMATS: Record<string, string> = {
  "date-yearly": "yyyy",
  "date-monthly": "MMM yyyy",
  "date-daily": "yyyy-MM-dd",
};

function getPickerTypeFromMetadata({
  displayType,
}: Metadata): PickerType | undefined {
  if (displayType === "date-monthly") {
    return "month";
  }
  if (displayType === "date-yearly") {
    return "year";
  }
  return undefined;
}

function DateInput({
  metadata,
  formLookupKey,
  templateKey,
  NotApplicableWrapper,
}: InputFieldProps): JSX.Element {
  const formRules = getFormRulesFromMetadata(metadata.value);

  const pickerType = getPickerTypeFromMetadata(metadata);

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useTemplateController({
    name: formLookupKey,
    ...(formRules && {
      rules: { ...formRules },
    }),
  });

  function handleChange(dayjs: Dayjs | null) {
    if (!dayjs) return;
    const date = dayjs.toDate();
    onChange(date);
  }

  const handleApplySuggestion = useCallback(
    (suggested: unknown) => {
      // Worker returns ISO date strings.
      if (typeof suggested !== "string" || !suggested) return;
      const parsed = dayjs(suggested);
      if (parsed.isValid()) {
        onChange(parsed.toDate());
      }
    },
    [onChange]
  );

  const formatValue = useCallback(
    (v: unknown) => {
      const fmt =
        DATE_DISPLAY_FORMATS[metadata.displayType ?? "date-daily"] ??
        "yyyy-MM-dd";
      try {
        return formatDate(v as string, fmt) || String(v);
      } catch {
        return String(v);
      }
    },
    [metadata.displayType]
  );

  return (
    <NotApplicableWrapper {...{ metadata, templateKey }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <DatePicker
          status={error && "error"}
          onChange={handleChange}
          allowClear={false}
          defaultValue={(value as Date) && dayjs(value as Date)}
          picker={pickerType}
        />
        {error && (
          <Tooltip title={error.message}>
            <ExclamationCircleOutlined style={{ color: "red" }} />
          </Tooltip>
        )}
        <FieldSuggestionChip
          templateKey={templateKey}
          hideMultiStateView={metadata.value.hideMultiStateView}
          onApply={handleApplySuggestion}
          formatValue={formatValue}
        />
      </div>
    </NotApplicableWrapper>
  );
}

export default DateInput;
